<template>
<div>
	<v-dialog
		v-model="dialog"
		persistent
		width="1000"
	>
		<v-card>
			<v-card-title
				class="headline light-blue darken-1 white--text"
				primary-title
			>
				{{$t('workers.job')}}
			</v-card-title>
			<v-card-text>
				<v-container grid-list-lg>
					<v-layout row>
						<v-flex sm6 md4>
							<v-text-field :label="$t('workers.work_permit_number')"
								v-model="jobData.work_permit_number"
							/>
						</v-flex>
						<v-flex sm6 md4>
							<Datepicker :label="$t('workers.work_permit_date')"
								v-model="jobData.work_permit_date"
							/>
						</v-flex>
						<v-flex sm6 md4>
							<v-checkbox :label="$t('workers.first_job')"
								v-model="jobData.first_job"
							/>
						</v-flex>
					</v-layout>
					<v-layout>
						<v-flex sm6 md4>
							<v-combobox
								v-model="jobData.cz_isco"
								item-value="key"
								item-text="value"
								:items="czIscos"
								:label="$t('workers.cz_isco')"
								persistent-hint
								return-object
								@input="changeCzIsco"
							>
								<template slot="selection" slot-scope="data">
									{{ data.item.key }} - {{ data.item.value }}
								</template>
								<template slot="item" slot-scope="data">
									{{ data.item.key }} - {{ data.item.value }}
								</template>
							</v-combobox>
						</v-flex>
						<v-flex sm6 md4>
							<v-combobox
								v-model="jobData.cz_nace"
								item-value="key"
								item-text="value"
								:items="czNaces"
								:label="$t('workers.cz_nace')"
								persistent-hint
								return-object
								@input="changeCzNace"
							>
								<template slot="selection" slot-scope="data">
									{{ data.item.key }} - {{ data.item.value }}
								</template>
								<template slot="item" slot-scope="data">
									{{ data.item.key }} - {{ data.item.value }}
								</template>
							</v-combobox>
						</v-flex>
						<v-flex sm6 md4>
							<v-select
								:label="$t('workers.job_form')"
								v-model="jobData.job_form"
								:items="jobTypes"
							/>
						</v-flex>
					</v-layout>
					<v-layout>
						<v-flex sm6 md6>
							<Datepicker
								:error-messages="validate.date"
								:label="$t('workers.job_start')"
								v-model="jobData.job_start"
							/>
						</v-flex>
						<v-flex sm6 md6>
							<Datepicker
								:error-messages="validate.date"
								:label="$t('workers.job_end')"
								v-model="jobData.job_end"
							/>
						</v-flex>
					</v-layout>
					<v-layout>
						<v-flex sm6 md4>
							<Datepicker :label="$t('workers.sign_date')"
								v-model="jobData.sign_date"
							/>
						</v-flex>
						<v-flex sm6 md4>
							<v-text-field :label="$t('workers.salary_min')"
								v-model="jobData.salary_min"
							/>
						</v-flex>
						<v-flex sm6 md4>
							<v-text-field :label="$t('workers.salary_max')"
								v-model="jobData.salary_max"
							/>
						</v-flex>
					</v-layout>
					<address-by-human
						:list-human="employers"
						:list-address="address"
						:labelHuman="$t('workers.employer')"
						:humanId="jobData.employer_id"
						:addressId="jobData.work_address_id"
						@update:humanId="updateEmployer"
						@update:addressId="updateAddress"
						@getAddress="getAddress"
						:errorHuman="validate.employer"
					/>
				</v-container>
			</v-card-text>

			<v-divider />

			<v-card-actions>
				<v-spacer />
				<v-btn
					color="red light-1"
					flat
					@click="cancel"
				>
					{{$t('common.cancel')}}
				</v-btn>
				<v-btn
					color="green light-1"
					flat
					@click="saveWithValidation"
				>
					{{$t('common.save')}}
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
	<v-tooltip top slot="activator">
		<v-btn
			slot="activator"
			@click="open"
			color="red"
			dark
			fab
		>
			<v-icon>add</v-icon>
		</v-btn>
		<span>{{$t('workers.add_job')}}</span>
	</v-tooltip>
</div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import Datepicker from '../../../../components/DatePicker'
import AddressByHuman from '../../../../components/address-by-human/AddressByHuman'
import { validateDataStartEnd } from '../../../../utils/helpers'

export default {
	data: vm => ({
		model: false,
		jobTypes: [vm.$t('workers.parttime'), vm.$t('workers.fulltime')],
		validation: {
			date: false
		}
	}),
	components: {
		Datepicker,
		AddressByHuman
	},
	props: {
		dialog: Boolean,
		employers: Array,
		jobData: {
			work_address_id: Number,
			employer_id: Number,
			cz_isco: String,
			cz_nace: String,
			job_start: String,
			job_end: String,
			job_form: String,
			salary_min: Number,
			salary_max: Number,
			sign_date: String,
			work_permit_number: String,
			work_permit_date: String,
			first_job: Boolean
		}
	},
	computed: {
		...mapGetters({
			czIscos: 'settings/czIscos',
			czNaces: 'settings/czNaces'
		}),
		...mapState({
			address: ({ employers }) => employers.detail.employer_work_address || []
		}),
		validate() {
			const { job_start, job_end } = this.jobData
			const date = validateDataStartEnd(job_start, job_end)
				? this.$t('workers.start_end_valid')
				: null

			return {
				date
			}
		},
	},
	methods: {
		...mapActions({
			getAddress: 'employers/getEmployer'
		}),
		changeCzNace(item) {
			const isHaveInList = this.czNaces.find((it) => item === it)
			this.jobData.cz_nace = isHaveInList ? item : ''
		},
		changeCzIsco(item) {
			const isHaveInList = this.czIscos.find((it) => item === it)
			this.jobData.cz_isco = isHaveInList ? item : ''
		},
		saveWithValidation() {
			const error= Object.values(this.validate).find(error => error)
			if (error) return false

			this.$emit('save', this.jobData)
		},
		cancel() {
			this.$emit('cancel')
			this.dialog = false
		},
		open() {
			this.$emit('open')
			this.dialog = true
		},
		updateEmployer(value) {
			this.$parent.propForm.jobData.employer_id = value
		},
		updateAddress(value) {
			this.$parent.propForm.jobData.work_address_id = value
		}
	}
}
</script>
